import { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import FloatingLabel from "react-bootstrap/FloatingLabel";

import Footer from "../components/Footer";
import Navbar from "../components/SpectraNavbar";
import ForgotPasswordModal from "../components/ui/ForgotPasswordModal";
import { ErrorText } from "../components/ui/ErrorReference";
import { getApiErrorInfo } from "../api/apiError";

import { useUserContext } from "../contexts";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/Login.css";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [validated, setValidated] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [alertFromRedirect, setAlertFromRedirect] = useState(null);

  const { login } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for alert passed via location state
  useEffect(() => {
    if (location.state?.alert) {
      setAlertFromRedirect(location.state.alert);
      // Clear the state to prevent showing alert on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      // setLoginError("Please fill out all required fields.");
      return;
    } else {
      try {
        await login(email, password);
        navigate("/");
      } catch (error) {
        console.error("Login failed:", error);
        setLoginError(getApiErrorInfo(error, "Login failed"));
      }
    }
  };

  const handleShowPassword = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div id='main-wrapper' className='flex-container'>
      <Navbar />
      <div
        className='h-100 flex-grow-1 d-flex flex-column align-items-center'
        style={{ paddingTop: "79px" }}
      >
        <div
          className='d-flex justify-content-center align-items-center container-fluid flex-grow-1 h-100'
          // style={{ paddingTop: "79px" }}
        >
          <div id='login-form' className='col-lg-8 col-12  bg-white p-5 border'>
            <header className='mb-4 text-center'>
              <h1 className='mb-1'>Welcome!</h1>
              <p className='text-muted fs-5'>Sign in to start testing</p>
            </header>
            <Form noValidate className='my-3' onSubmit={onSubmit}>
              {alertFromRedirect && (
                <Alert
                  variant={alertFromRedirect.variant}
                  dismissible
                  onClose={() => setAlertFromRedirect(null)}
                >
                  <i className='bi bi-exclamation-circle me-2'></i>
                  {alertFromRedirect.message}
                </Alert>
              )}
              {loginError && (
                <Alert variant='danger'>
                  <i className='bi bi-exclamation-circle me-2'></i>
                  <ErrorText error={loginError} />
                </Alert>
              )}
              <Form.Group className='mb-3' controlId='formBasicEmail'>
                <FloatingLabel label='Email Address'>
                  <Form.Control
                    className='login-input'
                    onChange={(e) => setEmail(e.target.value)}
                    type='email'
                    autoComplete='username'
                    placeholder='Email Address'
                    required
                    pattern='^[^\s@]+@[^\s@]+\.[^\s@]+$'
                    isInvalid={validated && (email === "" || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {email === "" ? "Please provide an email address." : "Please provide a valid email address."}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>

              <Form.Group className='mb-1' controlId='formBasicPassword'>
                <FloatingLabel label='Password'>
                  <Form.Control
                    type={isVisible ? "text" : "password"}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete='current-password'
                    placeholder='Password'
                    required
                    isInvalid={validated && password === ""}
                  />
                  <Form.Control.Feedback type='invalid'>
                    Please provide a password.
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Form.Group>

              <div className='d-flex justify-content-between align-items-center'>
                <Form.Check
                  type='checkbox'
                  label='Show Password'
                  id='show-password-checkbox'
                  onChange={handleShowPassword}
                  className='ms-2 w-50'
                />

                <button
                  type='button'
                  onClick={() => setShowForgotPasswordModal(true)}
                  className='btn btn-link float-end p-0'
                >
                  Forgot Password?
                </button>
              </div>

              <div className='d-grid'>
                <Button variant='primary' className='mt-4' type='submit'>
                  Sign in
                </Button>
              </div>
            </Form>
            <ForgotPasswordModal
              show={showForgotPasswordModal}
              handleClose={() => setShowForgotPasswordModal(false)}
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
